/**
 * RunAI OS — Dock (macOS-style)
 * - Pinned items: persist in localStorage, right-click → Remove from Dock
 * - Open windows: show temporarily even if not pinned (with dot indicator)
 * - Right-click → Keep in Dock / Remove from Dock
 * - Auto-hide: dock hides when not hovered, shows on mouse near edge
 * Rule 1: MAX 300 lines
 */
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../../../contexts/AuthContext'
import { useResponsive } from '../../../hooks/useResponsive'
import { useSideChatPosition } from '../SideChat/useSideChatPosition'
import { useWindowManager } from '../WindowManager/WindowManagerContext'
import { WINDOW_REGISTRY } from '../WindowManager/windowRegistry'
import { DOCK_ITEMS } from './dockConfig'
import DockDragHandle from './DockDragHandle'
import DockItem from './DockItem'
import { useDockPins } from './useDockPins'
import { useDockPosition, type DockPosition } from './useDockPosition'

export default function Dock() {
  const { user } = useAuth()
  const { isMobile } = useResponsive()
  const { openWindow, windows } = useWindowManager()
  const { position, setPos, isHorizontal } = useDockPosition()
  const { position: chatPos } = useSideChatPosition()
  const { pinnedItems, isPinned, pin, unpin, reorder } = useDockPins()
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)

  // Auto-hide state
  const [autoHide] = useState(() => localStorage.getItem('os-dock-autohide') === 'true')
  const [visible, setVisible] = useState(true)
  const dockRef = useRef<HTMLDivElement>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Edge detection threshold (px from screen edge to trigger show)
  const EDGE_THRESHOLD = 12

  const showDock = useCallback(() => {
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null }
    setVisible(true)
  }, [])

  const scheduleDockHide = useCallback(() => {
    if (!autoHide) return
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    hideTimerRef.current = setTimeout(() => setVisible(false), 800)
  }, [autoHide])

  // Mouse move listener for edge detection (auto-hide mode)
  useEffect(() => {
    if (!autoHide || isMobile) return
    const onMove = (e: MouseEvent) => {
      const { clientX, clientY } = e
      const vw = window.innerWidth
      const vh = window.innerHeight
      let nearEdge = false
      if (position === 'bottom') nearEdge = clientY >= vh - EDGE_THRESHOLD
      else if (position === 'top') nearEdge = clientY <= EDGE_THRESHOLD + 36 // account for menubar
      else if (position === 'left') nearEdge = clientX <= EDGE_THRESHOLD
      else nearEdge = clientX >= vw - EDGE_THRESHOLD

      if (nearEdge) showDock()
    }
    window.addEventListener('mousemove', onMove, { passive: true })
    return () => window.removeEventListener('mousemove', onMove)
  }, [autoHide, isMobile, position, showDock])

  // Hide dock initially if auto-hide is on and no hover
  useEffect(() => {
    if (autoHide && !isMobile) scheduleDockHide()
  }, [autoHide, isMobile, scheduleDockHide])

  // KURAL 114 — keyboard shortcut Cmd/Ctrl+1..9 launches the N-th dock item
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ index: number }>).detail
      if (!detail || typeof detail.index !== 'number') return
      const items = pinnedItems.length ? pinnedItems : DOCK_ITEMS
      const item = items[detail.index]
      if (!item) return
      const wid = (item as { windowId?: string; id: string }).windowId || item.id
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: wid }))
    }
    window.addEventListener('runai:dock-launch', handler)
    return () => window.removeEventListener('runai:dock-launch', handler)
  }, [pinnedItems])

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1440
  const chatTakesSide = chatPos === 'left' || chatPos === 'right'
  const baseSize = vw < 640 ? 32 : vw < 1024 ? 38 : vw >= 2560 ? 54 : 46
  const iconSize = isMobile ? 32 : chatTakesSide && isHorizontal ? Math.max(28, baseSize - 12) : baseSize

  const isActive = (id: string, path: string) =>
    windows.some(w => (w.id === id || w.id === path) && !w.minimized)

  const handleClick = (id: string, path: string, title: string, width = 860, height = 580, noPadding = false) => {
    const Component = WINDOW_REGISTRY[path]
    if (!Component) return

    const vw = window.innerWidth
    const vh = window.innerHeight
    const w = Math.min(width, Math.floor(vw * 0.85))
    const h = Math.min(height, Math.floor(vh * 0.80))
    const openCount = windows.length
    const x = Math.max(20, Math.floor((vw - w) / 2) + openCount * 20)
    const y = Math.max(20, Math.floor((vh - h) / 2) + openCount * 20)

    const WrappedComponent = () => (
      <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--os-muted)', fontSize: '13px' }}>Loading...</div>}>
        <Component />
      </Suspense>
    )

    openWindow({ id, title, component: WrappedComponent, position: { x, y }, size: { width: w, height: h }, noPadding, _registryPath: path, maximized: id === 'launchpad' } as any)
  }

  // Build dock item list:
  // 1. Pinned items (in order)
  // 2. Open windows that are NOT pinned (temporary)
  const openWindowIds = windows.map(w => w.id)

  // Find open windows not in pinned list
  const tempOpenItems = DOCK_ITEMS.filter(item =>
    !item.separator &&
    !isPinned(item.id) &&
    openWindowIds.some(wid => wid === item.id || wid === item.path)
  )

  const dockThickness = isMobile ? '48px' : chatTakesSide && isHorizontal ? '54px' : '72px'

  const containerStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--os-dock-bg)', backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)', flexShrink: 0, position: 'relative',
    transition: 'height 0.2s, width 0.2s, opacity 0.25s ease, transform 0.25s ease',
    opacity: visible ? 1 : 0,
    transform: !visible
      ? (position === 'bottom' ? 'translateY(100%)' :
         position === 'top' ? 'translateY(-100%)' :
         position === 'left' ? 'translateX(-100%)' : 'translateX(100%)')
      : 'translate(0)',
    pointerEvents: visible ? 'auto' : 'none',
    ...(isMobile
      ? { height: dockThickness, borderTop: '1px solid var(--os-border)', width: '100%' }
      : position === 'bottom' ? { height: dockThickness, borderTop: '1px solid var(--os-border)', width: '100%' } :
        position === 'top'    ? { height: dockThickness, borderBottom: '1px solid var(--os-border)', width: '100%' } :
        position === 'left'   ? { width: '72px', borderRight: '1px solid var(--os-border)', height: '100%', flexDirection: 'column' } :
                                { width: '72px', borderLeft: '1px solid var(--os-border)', height: '100%', flexDirection: 'column' }),
  }

  const pillStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '4px',
    padding: isHorizontal ? '8px 14px' : '14px 8px',
    background: 'var(--os-surface)', borderRadius: '18px',
    border: '1px solid var(--os-border)',
    flexDirection: isHorizontal ? 'row' : 'column',
    ...(isMobile ? { overflowX: 'auto', maxWidth: '100%' } : {}),
  }

  const renderItem = (item: typeof DOCK_ITEMS[0], isTemp = false) => {
    if (item.separator) {
      return (
        <div key={item.id} style={{
          ...(isHorizontal ? { width: '1px', height: '30px' } : { height: '1px', width: '30px' }),
          background: 'var(--os-border)', margin: isHorizontal ? '0 4px' : '4px 0',
        }} />
      )
    }
    if (item.adminOnly && !(user?.role === 'admin' || user?.role === 'both')) return null

    return (
      <div
        key={item.id}
        draggable={!isTemp}
        onDragStart={() => !isTemp && setDraggedId(item.id)}
        onDragOver={(e) => { e.preventDefault(); !isTemp && setDragOverId(item.id) }}
        onDragLeave={() => setDragOverId(null)}
        onDrop={() => { if (!isTemp && draggedId) { reorder(draggedId, item.id); setDraggedId(null); setDragOverId(null) } }}
        onDragEnd={() => { setDraggedId(null); setDragOverId(null) }}
        style={{
          opacity: draggedId === item.id ? 0.5 : 1,
          borderLeft: dragOverId === item.id && draggedId !== item.id ? '2px solid var(--os-accent)' : '2px solid transparent',
          transition: 'opacity 0.15s',
        }}
      >
        <DockItem
          item={item}
          active={isActive(item.id, item.path)}
          isPinned={isPinned(item.id)}
          isTemp={isTemp}
          onClick={() => handleClick(item.id, item.path, item.windowTitle ?? item.label, item.windowWidth, item.windowHeight, item.noPadding)}
          onPin={() => pin(item.id)}
          onUnpin={() => unpin(item.id)}
          dockPosition={position}
          baseSize={iconSize}
        />
      </div>
    )
  }

  return (
    <div
      ref={dockRef}
      style={containerStyle}
      onMouseEnter={showDock}
      onMouseLeave={scheduleDockHide}
    >
      <div style={pillStyle}>
        {/* Pinned items */}
        {pinnedItems.map(item => renderItem(item, false))}

        {/* Separator before temp items */}
        {tempOpenItems.length > 0 && (
          <div style={{
            ...(isHorizontal ? { width: '1px', height: '30px' } : { height: '1px', width: '30px' }),
            background: 'var(--os-border)', margin: isHorizontal ? '0 4px' : '4px 0',
          }} />
        )}

        {/* Temporarily open (not pinned) windows */}
        {tempOpenItems.map(item => renderItem(item, true))}

        {/* Drag handle */}
        <div style={{
          ...(isHorizontal ? { width: '1px', height: '30px' } : { height: '1px', width: '30px' }),
          background: 'var(--os-border)', margin: isHorizontal ? '0 4px' : '4px 0',
        }} />
        <DockDragHandle
          currentPosition={position}
          onDragStart={() => {}}
          onDragEnd={(newPos: DockPosition) => setPos(newPos)}
        />
      </div>
    </div>
  )
}
